import { ActionTree } from 'vuex'
import { ContentState, ContentItem, Consent } from './types'
import { RootState } from '../types'
import { fetchAsync } from '@/helpers/utils'
import config from 'config'
import { Logger } from '@/lib/logger'
import store from '..'

export const actions: ActionTree<ContentState, RootState> = {

  /**
  * Dispatch loadContent action
  */
  async loadConsent ({ state, rootGetters, commit, dispatch }) {
    commit('UPDATE_COUNTRY', rootGetters['LANG/getCountry'])
    commit('UPDATE_LANG', rootGetters['LANG/getLang'])

    if (rootGetters['API/isLogged']) {
      const legalStatus = await store.dispatch('API/getLegalStatus', { 'app_types[]': config.api.appType })
      const legalsInfos = legalStatus.data.body.consents as Consent

      commit('SET_CONSENT', legalsInfos)
      dispatch('handlePendingConsent')

      if (typeof legalsInfos[0].country === 'undefined') {
        commit('UPDATE_FORCED_COUNTRY', state.country)
        commit('NEED_COUNTRY_SELECTION', true)
      } else {
        commit('UPDATE_COUNTRY', legalsInfos[0].country)
      }
    }
  },

  handlePendingConsent ({ commit, state, rootGetters }) {
    const legals = config.content.legals
    let pendingConsent = false
    for (const key in legals) {
      const consent = state.consent.find(legalInfo => legalInfo.document_type === legals[key].document_type)
      if (typeof consent !== 'undefined') {
        pendingConsent = pendingConsent || consent.pending_consent
      }
    }
    commit('API/SET_SHOULD_ACCEPT', rootGetters['API/getShouldAccept'] || pendingConsent, { root: true })
  },

  /**
  * Dispatch loadContent action
  */
  async loadContent ({ state, dispatch, rootGetters }) {
    if (config.content.useBlob) {
      await dispatch('computeAzureHtml')
    } else {
      const tokenType = rootGetters['API/isLogged'] && state.forcedCountry === null ? 'Token' : 'NoToken'
      await dispatch(`compute${tokenType}ApiHtml`)
    }
  },

  async computeAzureHtml ({ state, commit, dispatch, rootGetters }) {
    const country = state.forcedCountry || store.getters['LANG/getCountry']
    const legals = config.content.legals

    // Handle hide GSC
    if (state.hideGsc) {
      delete legals.gsc
    }
    for (const key in legals) {
      const azureEndpoint = legals[key].endpoint
      Logger.groupCollapsed(`Load content: ${key}`)
      try {
        let content = ''
        const fallbackCountry = rootGetters['LANG/getFallbackCountry']
        try {
          const html = await fetchAsync(`${config.content.baseUrl}${country}/${azureEndpoint}.html`)
          Logger.info(`Download ${key} for ${country}`, 'Content', '', 'yellowgreen')()
          content = await html.text()
        } catch {
          try {
            const html = await fetchAsync(`${config.content.baseUrl}${fallbackCountry}/${azureEndpoint}.html`)
            Logger.info(`Fail to download ${key} in ${country}`, 'Content', `(fallback in ${fallbackCountry})`, 'yellowgreen')()
            content = await html.text()
          } catch (error) {
            Logger.error(error.message, 'Contents', { fallbackCountry, key }, 'yellowgreen')()
            throw error
          }
        }
        dispatch('setContent', { content, key })
      } catch {
        commit('SET_ERROR', {
          [key]: true
        })
      }
      Logger.groupEnd()
    }
  },

  async computeTokenApiHtml ({ state, commit, dispatch }) {
    const legals = config.content.legals
    for (const key in legals) {
      const consent = state.consent.find(legalInfo => legalInfo.document_type === legals[key].document_type)
      if (typeof consent !== 'undefined' && typeof consent.url !== 'undefined') {
        if (config.content.innerHtml) {
          try {
            const html = await fetchAsync(consent.url)
            Logger.info(`Fetch ${consent.document_type}`, 'Content', '', 'yellowgreen')()
            const content = await html.text()
            dispatch('setContent', { content, key })
          } catch (error) {
            Logger.error(error.message, 'Contents', consent.url, 'yellowgreen')()
            commit('SET_ERROR', {
              [key]: true
            })
          }
        } else {
          Logger.info(`Fetch ${key}`, 'Content', '', 'yellowgreen')()
          const content = consent.url
          dispatch('setContent', { content, key })
        }
      } else {
        Logger.info(`Nothing to fetch ${key}`, 'Content', '', 'yellowgreen')()
      }
    }
  },

  async computeNoTokenApiHtml ({ state, commit, dispatch }) {
    const legals = config.content.legals
    const legalsArray = Object.values(legals) as ContentItem[]
    const doctypes = legalsArray.map((legal: ContentItem) => legal.api_doctype).join(',')
    const country = state.forcedCountry || store.getters['LANG/getCountry']
    const legalStatus = await store.dispatch('API/getLegalUrl', {
      app: config.api.externalAppName,
      doctypes: doctypes.toUpperCase(),
      country: country
    })
    const legalsInfos = legalStatus.data.body
    for (const key in legals) {
      const legalInfo = legalsInfos[legals[key].api_doctype]
      Logger.groupCollapsed(`Load content: ${key}`)
      if (typeof legalInfo !== 'undefined' && typeof legalInfo.url !== 'undefined') {
        if (config.content.innerHtml) {
          try {
            const html = await fetchAsync(legalInfo.url)
            Logger.info(`Fetch ${key}`, 'Content', '', 'yellowgreen')()
            const content = await html.text()
            dispatch('setContent', { content, key })
          } catch (error) {
            Logger.error(error.message, 'Contents', legalInfo.url, 'yellowgreen')()
            commit('SET_ERROR', {
              [key]: true
            })
          }
        } else {
          Logger.info(`Fetch ${key}`, 'Content', '', 'yellowgreen')()
          const content = legalInfo.url
          dispatch('setContent', { content, key })
        }
      } else {
        Logger.info(`Nothing to fetch ${key}`, 'Content', '', 'yellowgreen')()
      }
      Logger.groupEnd()
    }
  },

  setContent ({ state, commit }, { content, key }) {
    const document = new DOMParser().parseFromString(content, 'text/xml')
    const queryResult = document.querySelectorAll('[lang]')
    const elements = [].slice.call(queryResult)
    if (elements.length > 0) {
      for (const element of elements) {
        const langElement = element as HTMLElement
        const lang = langElement.getAttribute(('lang'))
        if (lang !== null) {
          const content = {} as { [key: string]: string }
          content[key] = langElement.outerHTML
          commit('SET_CONTENT', { lang: lang, content: content })
        }
      }
    } else {
      const langContent = {} as { [key: string]: string }
      langContent[key] = content
      commit('SET_CONTENT', { lang: state.selectedLanguage, content: langContent })
    }
  },

  async validateCountry ({ commit }) {
    commit('NEED_COUNTRY_SELECTION', false)
  }
}
